import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { BlogPost } from "@/entities/BlogPost";
import { Comment } from "@/entities/Comment";
import { motion } from "framer-motion";
import { Calendar, User, ArrowLeft, MessageCircle, ShoppingCart, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ReactMarkdown from "react-markdown";

export default function BlogPostPage() {
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentForm, setCommentForm] = useState({ author_name: "", author_email: "", content: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const postId = urlParams.get("id");
    if (postId) {
      loadPost(postId);
      loadComments(postId);
    }
  }, []);

  const loadPost = async (id) => {
    const fetchedPosts = await BlogPost.filter({ id }, "", 1);
    if (fetchedPosts.length > 0) {
      setPost(fetchedPosts[0]);
      await BlogPost.update(id, { views: (fetchedPosts[0].views || 0) + 1 });
    }
  };

  const loadComments = async (postId) => {
    const fetchedComments = await Comment.filter({ blog_post_id: postId, status: "approved" }, "-created_date", 100);
    setComments(fetchedComments);
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    await Comment.create({
      blog_post_id: post.id,
      ...commentForm
    });

    setCommentForm({ author_name: "", author_email: "", content: "" });
    setIsSubmitting(false);
    
    const urlParams = new URLSearchParams(window.location.search);
    const postId = urlParams.get("id");
    loadComments(postId);
  };

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Image */}
      {post.featured_image && (
        <div className="w-full h-96 overflow-hidden bg-gray-900">
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-full object-cover opacity-90"
          />
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Link to={createPageUrl("Blog")} className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 mb-8 font-semibold">
          <ArrowLeft className="w-5 h-5" />
          Back to Blog
        </Link>

        {/* Article Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Badge className="mb-4 bg-red-600 hover:bg-red-600 text-lg px-4 py-1">
            {post.category}
          </Badge>
          
          <h1 className="text-5xl font-black text-gray-900 mb-6 leading-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {post.title}
          </h1>

          <div className="flex items-center gap-6 text-gray-600 mb-8 pb-8 border-b">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5" />
              <span className="font-semibold">{post.author_name || "LCTX Team"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{new Date(post.created_date).toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              <span>{comments.length} Comments</span>
            </div>
          </div>

          {/* CTA Banner - Top */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-12"
          >
            <Card className="bg-gradient-to-r from-red-600 to-red-700 border-0 shadow-2xl overflow-hidden">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-6">
                    <img 
                      src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68e21bb4c1e25d4ab005735e/75fd5a943_1.jpg"
                      alt="LCTX Tyre Changer"
                      className="w-24 h-24 object-cover rounded-lg shadow-xl"
                      loading="lazy"
                    />
                    <div className="text-white">
                      <h3 className="text-2xl font-black mb-2">LCTX Air-Powered Tyre Changer</h3>
                      <p className="text-red-100 text-lg mb-2">Revolutionary portable tyre changing solution</p>
                      <div className="flex items-center gap-3">
                        <span className="text-3xl font-black">$599</span>
                        <Badge className="bg-white text-red-600 hover:bg-white">FREE Shipping</Badge>
                      </div>
                    </div>
                  </div>
                  <Link to={createPageUrl("Product")}>
                    <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 h-14 px-8 text-lg font-bold shadow-xl">
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      Order Now
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Article Content with Enhanced Styling */}
          <div className="prose prose-lg max-w-none mb-12">
            <style>{`
              @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
              
              .prose {
                font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              }
              .prose h1 {
                font-size: 2.75rem;
                font-weight: 900;
                color: #111827;
                margin-top: 3rem;
                margin-bottom: 1.5rem;
                line-height: 1.2;
                font-family: 'Poppins', sans-serif;
                background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                background-clip: text;
              }
              .prose h2 {
                font-size: 2.25rem;
                font-weight: 800;
                color: #1f2937;
                margin-top: 2.5rem;
                margin-bottom: 1rem;
                border-bottom: 4px solid #dc2626;
                padding-bottom: 0.5rem;
                font-family: 'Poppins', sans-serif;
              }
              .prose h3 {
                font-size: 1.75rem;
                font-weight: 700;
                color: #374151;
                margin-top: 2rem;
                margin-bottom: 0.75rem;
                font-family: 'Poppins', sans-serif;
              }
              .prose h4 {
                font-size: 1.35rem;
                font-weight: 700;
                color: #4b5563;
                margin-top: 1.5rem;
                margin-bottom: 0.5rem;
                font-family: 'Poppins', sans-serif;
              }
              .prose p {
                line-height: 1.8;
                margin-bottom: 1.5rem;
                color: #374151;
                font-size: 1.125rem;
                font-weight: 400;
              }
              .prose strong {
                color: #dc2626;
                font-weight: 700;
              }
              .prose ul, .prose ol {
                margin: 1.5rem 0;
                padding-left: 2rem;
              }
              .prose li {
                margin-bottom: 0.75rem;
                line-height: 1.7;
              }
              .prose table {
                width: 100%;
                border-collapse: collapse;
                margin: 2rem 0;
                box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                border-radius: 0.5rem;
                overflow: hidden;
              }
              .prose th {
                background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
                color: white;
                padding: 1rem;
                text-align: left;
                font-weight: 700;
                font-size: 1rem;
              }
              .prose td {
                padding: 0.75rem 1rem;
                border-bottom: 1px solid #e5e7eb;
              }
              .prose tr:nth-child(even) {
                background-color: #f9fafb;
              }
              .prose blockquote {
                border-left: 4px solid #dc2626;
                padding-left: 1.5rem;
                margin: 2rem 0;
                font-style: italic;
                color: #6b7280;
                background: #f9fafb;
                padding: 1.5rem;
                border-radius: 0.5rem;
                font-size: 1.15rem;
              }
              .prose code {
                background: #fee2e2;
                color: #dc2626;
                padding: 0.25rem 0.5rem;
                border-radius: 0.25rem;
                font-size: 0.9em;
                font-weight: 600;
              }
              .prose a {
                color: #dc2626;
                font-weight: 600;
                text-decoration: underline;
              }
              .prose a:hover {
                color: #991b1b;
              }
              .prose hr {
                margin: 3rem 0;
                border: none;
                height: 2px;
                background: linear-gradient(90deg, transparent, #dc2626, transparent);
              }
            `}</style>
            <ReactMarkdown
              components={{
                h2: ({node, ...props}) => (
                  <div>
                    <h2 {...props} />
                    {/* Insert relevant content image after H2 */}
                    {Math.random() > 0.6 && (
                      <div className="my-8 rounded-xl overflow-hidden shadow-2xl">
                        <img 
                          src={[
                            "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1200&h=600&fit=crop",
                            "https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=1200&h=600&fit=crop",
                            "https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=1200&h=600&fit=crop"
                          ][Math.floor(Math.random() * 3)]}
                          alt="Tyre Service Professional"
                          className="w-full h-auto"
                        />
                      </div>
                    )}
                  </div>
                ),
                h3: ({node, ...props}) => (
                  <div>
                    <h3 {...props} />
                    {Math.random() > 0.75 && (
                      <div className="my-6 rounded-xl overflow-hidden shadow-xl">
                        <img 
                          src={[
                            "https://images.unsplash.com/photo-1625047509248-ec889cbff17f?w=1200&h=500&fit=crop",
                            "https://images.unsplash.com/photo-1632823469522-ca101c2ebc14?w=1200&h=500&fit=crop",
                            "https://images.unsplash.com/photo-1590650516494-0c8e4a4dd67e?w=1200&h=500&fit=crop"
                          ][Math.floor(Math.random() * 3)]}
                          alt="Automotive Service Detail"
                          className="w-full h-auto"
                        />
                      </div>
                    )}
                  </div>
                ),
              }}
            >
              {post.content}
            </ReactMarkdown>

            {/* Mid-Content CTA */}
            <div className="my-16">
              <Card className="bg-gradient-to-br from-gray-900 to-black border-0 shadow-2xl">
                <CardContent className="p-10">
                  <div className="text-center">
                    <h3 className="text-3xl font-black text-white mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      🚀 Ready to Start Changing Tyres Anywhere?
                    </h3>
                    <p className="text-xl text-gray-300 mb-6">
                      Join 500+ satisfied customers with the LCTX portable tyre changer
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                      <Link to={createPageUrl("Product")}>
                        <Button size="lg" className="bg-red-600 hover:bg-red-700 h-16 px-10 text-xl font-bold shadow-xl">
                          <ShoppingCart className="w-6 h-6 mr-2" />
                          Buy Now - $599
                        </Button>
                      </Link>
                      <Link to={createPageUrl("Product")}>
                        <Button size="lg" variant="outline" className="bg-white text-gray-900 hover:bg-gray-100 h-16 px-10 text-xl font-bold">
                          Learn More
                          <ArrowRight className="w-6 h-6 ml-2" />
                        </Button>
                      </Link>
                    </div>
                    <p className="text-gray-400 mt-4">✅ FREE Shipping • ✅ 30-Day Warranty • ✅ Secure Checkout</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Bottom CTA Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 shadow-xl">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <h3 className="text-3xl font-black text-gray-900 mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
                      🎉 Special Offer: Save $300 Today!
                    </h3>
                    <p className="text-lg text-gray-700 mb-6">
                      Get the LCTX Air-Powered Tyre Changer at our lowest price ever. Limited time offer!
                    </p>
                    <ul className="space-y-2 mb-6">
                      {[
                        "✅ No Electricity Required",
                        "✅ Handles 12-21\" Tyres",
                        "✅ Only 80 lbs - True Portability",
                        "✅ 2 Ton Pneumatic Power",
                        "✅ FREE Shipping Included"
                      ].map((feature, idx) => (
                        <li key={idx} className="text-gray-700 font-medium">{feature}</li>
                      ))}
                    </ul>
                    <Link to={createPageUrl("Product")}>
                      <Button size="lg" className="bg-red-600 hover:bg-red-700 h-14 px-8 text-lg font-bold">
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Order Now - $599
                      </Button>
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <img 
                      src="https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&h=300&fit=crop"
                      alt="Tyre Service 1"
                      className="w-full h-48 object-cover rounded-lg shadow-lg"
                    />
                    <img 
                      src="https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=400&h=300&fit=crop"
                      alt="Tyre Service 2"
                      className="w-full h-48 object-cover rounded-lg shadow-lg"
                    />
                    <img 
                      src="https://images.unsplash.com/photo-1625047509248-ec889cbff17f?w=400&h=300&fit=crop"
                      alt="Tyre Service 3"
                      className="w-full h-48 object-cover rounded-lg shadow-lg"
                    />
                    <img 
                      src="https://images.unsplash.com/photo-1632823469522-ca101c2ebc14?w=400&h=300&fit=crop"
                      alt="Tyre Service 4"
                      className="w-full h-48 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Tags */}
          {post.tags && (
            <div className="flex flex-wrap gap-3 mb-12 pb-12 border-b">
              {post.tags.split(",").map((tag, idx) => (
                <Badge key={idx} variant="outline" className="text-sm px-3 py-1">
                  #{tag.trim()}
                </Badge>
              ))}
            </div>
          )}
        </motion.div>

        {/* Comments Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-black text-gray-900 mb-8" style={{ fontFamily: "'Poppins', sans-serif" }}>
            💬 Comments ({comments.length})
          </h2>

          {/* Comment Form */}
          <Card className="mb-8 border-2 border-gray-200">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold mb-4">Leave a Comment</h3>
              <form onSubmit={handleCommentSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={commentForm.author_name}
                      onChange={(e) => setCommentForm({...commentForm, author_name: e.target.value})}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={commentForm.author_email}
                      onChange={(e) => setCommentForm({...commentForm, author_email: e.target.value})}
                      required
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="comment">Comment *</Label>
                  <Textarea
                    id="comment"
                    value={commentForm.content}
                    onChange={(e) => setCommentForm({...commentForm, content: e.target.value})}
                    required
                    rows={4}
                    className="mt-1"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {isSubmitting ? "Posting..." : "Post Comment"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Comments List */}
          <div className="space-y-6">
            {comments.map((comment) => (
              <motion.div
                key={comment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-50 rounded-xl p-6 border border-gray-200"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {comment.author_name[0].toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-bold text-gray-900">{comment.author_name}</span>
                      <span className="text-sm text-gray-500">
                        {new Date(comment.created_date).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{comment.content}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <Card className="bg-gradient-to-r from-red-600 to-red-700 border-0 shadow-2xl">
          <CardContent className="p-10 text-center text-white">
            <h3 className="text-4xl font-black mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
              🔧 Start Changing Tyres Today!
            </h3>
            <p className="text-xl mb-8 text-red-100">
              Join the portable tyre changing revolution with LCTX
            </p>
            <Link to={createPageUrl("Product")}>
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 h-16 px-12 text-xl font-bold shadow-xl">
                <ShoppingCart className="w-6 h-6 mr-2" />
                Buy Now - $599
              </Button>
            </Link>
            <p className="mt-6 text-red-100">
              ✅ FREE Shipping • ✅ 30-Day Warranty • ✅ 500+ Happy Customers
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}